import React, { useState, useEffect } from 'react';

// Enhanced Input cell with validation error display
const InputCell = ({
  value,
  onChange,
  type = 'text',
  placeholder = '',
  fieldName,
  rowId,
  required = false,
  validationError = null,
  inputMode,
  maxLength
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [showError, setShowError] = useState(false);
  const inputId = `${fieldName?.toLowerCase()}-${rowId}`;
  const errorId = `${inputId}-error`;

  // Sync external changes (e.g., duplication) into local state
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  // Show validation errors
  useEffect(() => {
    if (validationError) {
      setShowError(true);
      const timer = setTimeout(() => setShowError(false), 5000); // Hide after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [validationError]);

  const handleKeyDown = (e) => {
    if (e.ctrlKey && (e.key === 'c' || e.key === 'v' || e.key === 'a')) {
      return;
    }
    // Enter key commits the change
    if (e.key === 'Enter') {
      commitChange();
    }
    // Escape key reverts changes
    if (e.key === 'Escape') {
      setLocalValue(value || '');
    }
  };

  const handleChange = (e) => {
    setLocalValue(e.target.value);
    // Clear error when user starts typing
    if (showError) {
      setShowError(false);
    }
  };

  const commitChange = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const hasError = validationError && showError;

  return (
    <div className="relative">
      <input
        id={inputId}
        type={type}
        value={localValue}
        onChange={handleChange}
        onBlur={commitChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || `Enter ${fieldName}`}
        className={`w-full px-3 py-2 border-2 rounded-lg text-xs font-medium bg-white transition-all duration-200 focus:outline-none focus:ring-4 ${
          hasError
            ? 'border-red-400 bg-red-50 text-red-800 focus:ring-red-200 focus:border-red-600'
            : 'border-gray-300 hover:border-gray-400 focus:ring-blue-200 focus:border-blue-600 focus:bg-blue-50'
        } ${fieldName === 'Width' || fieldName === 'Height' ? 'max-w-[110px] tracking-widest text-center' : ''}`}
        aria-label={`${fieldName} for row ${rowId}`}
        aria-required={required}
        inputMode={inputMode}
        maxLength={maxLength}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
      />
      {hasError && (
        <div 
          id={errorId}
          className="absolute -bottom-5 left-0 right-0 text-red-700 text-[10px] font-medium truncate bg-white px-1 rounded shadow-sm border border-red-200"
          role="alert"
          aria-live="polite"
        >
          {validationError}
        </div>
      )}
    </div>
  );
};

export default InputCell;